import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyListExecutiveGroupsQuery } from '../../features/apiSlice/groupApi';
import './styles.scss';

export default function ExecutiveGroupSelect({
  value,
  onChange,
  placeholder = 'Select a territory group...',
  className = '',
  disabled = false,
  includeEmptyOption = true
}) {
  const [trigger] = useLazyListExecutiveGroupsQuery();
  const [groups, setGroups] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState(null);

  const loadGroups = useCallback(async (isCancelled = () => false) => {
    setIsFetching(true);
    setError(null);

    try {
      const response = await trigger().unwrap();
      if (isCancelled()) return;

      const records = Array.isArray(response?.data) ? response.data : [];
      setGroups(records
        .filter(group => group?.type === 'territory')
        .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        ));
    } catch (requestError) {
      if (!isCancelled()) {
        setGroups([]);
        setError(requestError);
      }
    } finally {
      if (!isCancelled()) setIsFetching(false);
    }
  }, [trigger]);

  useEffect(() => {
    let cancelled = false;
    loadGroups(() => cancelled);
    return () => { cancelled = true; };
  }, [loadGroups]);

  const isError = Boolean(error);
  const isSelectDisabled = disabled || isFetching;

  const handleChange = (event) => {
    const id = event.target.value || null;
    onChange?.(id);
  };

  const retry = () => loadGroups();

  return (
    <div className={`exec-group-select ${className}`} data-testid="exec-group-select">
      <div className="exec-group-select__control">
        <select
          id="exec-group-select__select"
          className="exec-group-select__select"
          value={value || ''}
          onChange={handleChange}
          disabled={isSelectDisabled}
          aria-invalid={isError}
          onFocus={() => { if (isError) retry(); }}
          onClick={() => { if (isError) retry(); }}
        >
          {includeEmptyOption && (
            <option value="">
              {isFetching ? 'Loading...' : (isError ? 'Retry loading...' : placeholder)}
            </option>
          )}
          {!isFetching && !isError && groups.map(group => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </select>

        {isFetching && <span className="exec-group-select__hint">Loading groups...</span>}
        {!isFetching && !isError && groups.length === 0 && (
          <span className="exec-group-select__hint">No territory groups found</span>
        )}
        {isError && (
          <span
            className="exec-group-select__error"
            role="button"
            tabIndex={0}
            onClick={retry}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') retry();
            }}
            title="Click to retry"
          >
            {error?.message || 'Failed to load groups - click to retry'}
          </span>
        )}
      </div>
    </div>
  );
}

ExecutiveGroupSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  includeEmptyOption: PropTypes.bool
};
